/**
 * OCRSourceSelectionModal
 * Modal for selecting the source of an image/document for OCR processing on mobile.
 * Provides options to take a photo, choose from photo library, or choose from files.
 */

import { X, Camera, Image, FileText } from 'lucide-react-native';
import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Platform,
} from 'react-native';

export type OCRSourceOption = 'camera' | 'library' | 'files';

export interface OCRSourceSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (option: OCRSourceOption) => void;
}

export const OCRSourceSelectionModal: React.FC<
  OCRSourceSelectionModalProps
> = ({ visible, onClose, onSelect }) => {
  const handleSelect = (option: OCRSourceOption) => {
    onSelect(option);
    onClose();
  };

  // This modal is only for mobile platforms
  if (Platform.OS === 'web') {
    return null;
  }

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={onClose}
      >
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Create Request from Document</Text>
            <TouchableOpacity
              onPress={onClose}
              accessibilityLabel="Close modal"
              accessibilityRole="button"
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <X size={24} color="#666" />
            </TouchableOpacity>
          </View>

          <Text style={styles.description}>
            Choose how you'd like to provide your document for AI extraction
          </Text>

          {/* Options */}
          <View style={styles.optionsContainer}>
            {/* Take Photo Option */}
            <TouchableOpacity
              style={styles.option}
              onPress={() => handleSelect('camera')}
              accessibilityLabel="Take a photo"
              accessibilityRole="button"
            >
              <View style={styles.iconContainer}>
                <Camera size={28} color="#1C3A70" />
              </View>
              <View style={styles.optionTextContainer}>
                <Text style={styles.optionTitle}>Take Photo</Text>
                <Text style={styles.optionDescription}>
                  Capture a document using your camera
                </Text>
              </View>
            </TouchableOpacity>

            {/* Choose from Photos Option */}
            <TouchableOpacity
              style={styles.option}
              onPress={() => handleSelect('library')}
              accessibilityLabel="Choose from photos"
              accessibilityRole="button"
            >
              <View style={styles.iconContainer}>
                <Image size={28} color="#1C3A70" />
              </View>
              <View style={styles.optionTextContainer}>
                <Text style={styles.optionTitle}>Choose from Photos</Text>
                <Text style={styles.optionDescription}>
                  Select an image from your photo library
                </Text>
              </View>
            </TouchableOpacity>

            {/* Choose from Files Option */}
            <TouchableOpacity
              style={styles.option}
              onPress={() => handleSelect('files')}
              accessibilityLabel="Choose from files"
              accessibilityRole="button"
            >
              <View style={styles.iconContainer}>
                <FileText size={28} color="#1C3A70" />
              </View>
              <View style={styles.optionTextContainer}>
                <Text style={styles.optionTitle}>Choose from Files</Text>
                <Text style={styles.optionDescription}>
                  Select a PDF, image, or text file from your files
                </Text>
              </View>
            </TouchableOpacity>
          </View>

          {/* Cancel Button */}
          <TouchableOpacity
            style={styles.cancelButton}
            onPress={onClose}
            accessibilityLabel="Cancel"
            accessibilityRole="button"
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1C3A70',
    flex: 1,
  },
  description: {
    fontSize: 14,
    color: '#666',
    paddingHorizontal: 20,
    paddingBottom: 16,
    lineHeight: 20,
  },
  optionsContainer: {
    paddingHorizontal: 12,
    paddingBottom: 12,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginVertical: 4,
    borderRadius: 12,
    backgroundColor: '#F8F9FA',
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#E8EEF6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  optionTextContainer: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  optionDescription: {
    fontSize: 13,
    color: '#666',
  },
  cancelButton: {
    paddingVertical: 16,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
});
